import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { PublisherConfig, SelfServiceFormData } from '../types/selfService';
import {
  checkCampaignFeasibility,
  type CampaignFeasibilityResponse,
} from './feasibilityService';

const DEBOUNCE_MS = 350;
const SPINNER_DELAY_MS = 200;

export interface UseCampaignFeasibilityResult {
  verdict: CampaignFeasibilityResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * Live feasibility hook for the Campaign Setup step. Fires `/inventory/
 * campaign-feasibility` whenever budget / dates / payment plan / targeting
 * inputs are all valid; debounced 350ms; aborts in-flight requests on input
 * change; guards against out-of-order responses; defers the spinner so a
 * sub-200ms response doesn't flicker.
 *
 * The hook does NOT block continue/submit on its own — it only surfaces a
 * verdict. The wizard reads `verdict.feasible` and (CTV-474) per-flight
 * `constraintKind` / `geoShortfall` to render the warning card.
 */
export function useCampaignFeasibility(
  config: PublisherConfig,
  excludeSubmissionId?: string,
): UseCampaignFeasibilityResult {
  const { watch } = useFormContext<SelfServiceFormData>();
  const budget = watch('budget');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const paymentPlan = watch('paymentPlan');
  const targetingMode = watch('targetingMode');
  const geoMode = watch('geoMode');
  const radiusZips = watch('geoTargetZips');
  const explicitDMAs = watch('selectedDMAs' as keyof SelfServiceFormData) as unknown as string[] | undefined;
  const explicitStates = watch('selectedStates' as keyof SelfServiceFormData) as unknown as string[] | undefined;

  // Resolve the active ZIP list for the inventory check.
  // CTV-502: ZIP targeting now flows through radius mode only — the
  // standalone selectedZIPs arm was removed with the ZIPs tab.
  const effectiveZips = useMemo(() => {
    if (targetingMode !== 'custom') return undefined;
    if (geoMode === 'radius' && Array.isArray(radiusZips) && radiusZips.length > 0) {
      return radiusZips;
    }
    return undefined;
  }, [targetingMode, geoMode, radiusZips]);

  // CTV-474: DMA + State geo modes now route through the geo-aware feasibility
  // model. Each is only populated when its mode is active.
  const effectiveDMAs = useMemo(() => {
    if (targetingMode !== 'custom' || geoMode !== 'dmas') return undefined;
    return Array.isArray(explicitDMAs) && explicitDMAs.length > 0 ? explicitDMAs : undefined;
  }, [targetingMode, geoMode, explicitDMAs]);

  const effectiveStates = useMemo(() => {
    if (targetingMode !== 'custom' || geoMode !== 'states') return undefined;
    return Array.isArray(explicitStates) && explicitStates.length > 0 ? explicitStates : undefined;
  }, [targetingMode, geoMode, explicitStates]);

  const productId = config.products[0]?.id;

  // Stable serialized key — avoids re-fires on reference equality of identical
  // arrays. Each list is sorted so [10001, 30303] and [30303, 10001] hash equal.
  const inputsKey = useMemo(
    () =>
      JSON.stringify({
        productId,
        budget,
        startDate,
        endDate,
        paymentPlan,
        targetingMode,
        geoTargetZips: effectiveZips ? [...effectiveZips].sort() : null,
        geoTargetDMAs: effectiveDMAs ? [...effectiveDMAs].sort() : null,
        geoTargetStates: effectiveStates ? [...effectiveStates].sort() : null,
        excludeSubmissionId,
      }),
    [
      productId,
      budget,
      startDate,
      endDate,
      paymentPlan,
      targetingMode,
      effectiveZips,
      effectiveDMAs,
      effectiveStates,
      excludeSubmissionId,
    ],
  );

  const [verdict, setVerdict] = useState<CampaignFeasibilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Gate: all required inputs must be valid before firing. Payment plan is
    // not part of the gate — the buyer only picks it on the review step, but
    // the campaign step needs live feasibility immediately. We pass 'upfront'
    // as the default below; monthly doesn't change inventory math.
    if (!productId || !budget || budget <= 0 || !startDate || !endDate) {
      setVerdict(null);
      setLoading(false);
      setError(null);
      return;
    }
    // Reject obviously-backwards date ranges before burning a network call.
    if (endDate <= startDate) {
      setVerdict(null);
      setLoading(false);
      setError(null);
      return;
    }
    // CTV-570 v2 UX fix: when the buyer picks "Target by Location" but
    // hasn't chosen any zips / DMAs / states yet, the backend would
    // silently evaluate as station-wide default — producing a misleading
    // verdict. Skip the fetch entirely. The resolver + Continue-gate
    // already surface an "add a location" error chip, so no verdict is
    // the right state here.
    if (
      targetingMode === 'custom' &&
      !effectiveZips &&
      !effectiveDMAs &&
      !effectiveStates
    ) {
      setVerdict(null);
      setLoading(false);
      setError(null);
      return;
    }

    // CTV-474: clear the verdict immediately on input change so the warning
    // banner animates *out* before the debounced request fires. It comes
    // back when the new verdict arrives and is still negative.
    setVerdict(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const reqId = ++requestIdRef.current;

      // Defer spinner: avoids UI flicker for sub-200ms responses.
      if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      spinnerTimerRef.current = setTimeout(() => {
        if (reqId === requestIdRef.current) setLoading(true);
      }, SPINNER_DELAY_MS);

      checkCampaignFeasibility(
        {
          productId: productId!,
          budget: budget!,
          startDate,
          endDate,
          paymentPlan: paymentPlan ?? 'upfront',
          targetingMode: targetingMode ?? 'default',
          geoTargetZips: effectiveZips,
          geoTargetDMAs: effectiveDMAs,
          geoTargetStates: effectiveStates,
          excludeSubmissionId,
          // Dev-only: ask the API for the per-line-item booked breakdown.
          // Prod builds drop this flag at the bundler.
          includeDiagnostic: import.meta.env.DEV,
        },
        ctrl.signal,
      )
        .then((data) => {
          if (reqId !== requestIdRef.current) return;
          setVerdict(data);
          setError(null);
        })
        .catch((e) => {
          if (reqId !== requestIdRef.current) return;
          const name = (e as { name?: string })?.name;
          if (name === 'AbortError' || name === 'CanceledError') return;
          setError('We could not check inventory just now. Try again in a moment.');
          setVerdict(null);
        })
        .finally(() => {
          if (spinnerTimerRef.current) {
            clearTimeout(spinnerTimerRef.current);
            spinnerTimerRef.current = null;
          }
          if (reqId === requestIdRef.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey]);

  return { verdict, loading, error };
}
